import { useLocation, useNavigate, useParams } from 'react-router'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import clsx from 'clsx'
import {
  IconBell,
  IconChevronLeft,
  IconGitCompare,
  IconMapPin,
  IconStar,
  IconArrowDown,
  IconArrowUp,
  IconPoint,
} from '@tabler/icons-react'
import { AspectRatio, Button, Image, Text } from '@mantine/core'
import Meta from '../../common/Meta'
import classes from './TeamComponents.module.css'
import UpdatedBar from '../../common/components/header/UpdatedBar'
import { useTeamRanks } from '../../ranks/api'
import Stat from '../../stories/Stat/Stat'
import RaceRankDisplay from './RaceRankDisplay'
import MatchRankDisplay from './MatchRankDisplay'
import { Player } from '../../players/api'
import Breakdown from '../../stories/Breakdown/Breakdown'

export default function TeamHeader({
  team_name,
  team_location,
  team_logo,
  competitionKind,
  singlePlayerTeams,
  singlePlayer,
}: {
  team_name: string
  team_location?: string
  team_logo: string
  competitionKind?: 'match' | 'race'
  singlePlayerTeams?: boolean
  singlePlayer?: Player
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const navigate = useNavigate()
  const location = useLocation()
  const { competitionId, teamId } = useParams()
  const [
    pointBreakdownOpen,
    { open: openPointBreakdown, close: closePointBreakdown },
  ] = useDisclosure(false)

  const { data: teamRanks } = useTeamRanks({
    competition_id: Number(competitionId),
    team_id: Number(teamId),
    page: 1,
    page_size: 1,
  })

  const teamRank = teamRanks.items[0]

  const getRankChangeIcon = (change: number) => {
    if (change > 0) return IconArrowUp
    if (change < 0) return IconArrowDown
    return IconPoint
  }

  const getRankChangeColor = (change: number) => {
    if (change > 0) return 'green'
    if (change < 0) return 'red'
    return 'gray'
  }

  const showPlayerDisplay = singlePlayerTeams && singlePlayer
  const displayName = showPlayerDisplay ? singlePlayer.name : team_name
  const displayImage = showPlayerDisplay ? singlePlayer.photo.main : team_logo
  const isPlaceholderPhoto = showPlayerDisplay
    ? /\.svg$/i.test(singlePlayer.photo.main)
    : false

  if (isDesktop) {
    return (
      <div className={classes.header_container}>
        <Meta title={team_name} />
        <div className={classes.header_container_wrapper}>
          <IconChevronLeft
            className={classes.back_icon}
            onClick={() =>
              navigate(
                location.state?.from?.pathname ||
                  `/competitions/${Number(competitionId)}/teams`
              )
            }
          />

          <Button
            className={classes.compare_button}
            leftSection={<IconGitCompare className={classes.compare_icon} />}
            onClick={() =>
              navigate(
                `/competitions/${competitionId}/compare?ltype=team&lid=${teamId}`
              )
            }
          >
            Compare
          </Button>
        </div>
        <div>
          <div className={classes.team_header_container}>
            <div className={classes.team_header_wrapper}>
              <AspectRatio
                ratio={1 / 1}
                className={
                  showPlayerDisplay ? classes.aspect_ratio_player : undefined
                }
              >
                <Image
                  src={displayImage}
                  alt={displayName}
                  className={
                    showPlayerDisplay ? classes.player_image : classes.team_logo
                  }
                  fit={isPlaceholderPhoto ? 'contain' : 'scale-down'}
                />
              </AspectRatio>
              <div className={classes.team_info_container}>
                <Text component="h3" className={classes.team_name_header}>
                  {displayName}
                </Text>
                {team_location && (
                  <Text className={classes.team_location}>
                    <IconMapPin size="12" /> {team_location}
                  </Text>
                )}
              </div>
            </div>
            {teamRank && (
              <div className={classes.header_widgets_wrapper}>
                <Stat
                  title="Overall Rank"
                  value={teamRank?.rank ?? null}
                  icon={getRankChangeIcon(teamRank?.rank_change ?? 0)}
                  iconColor={getRankChangeColor(teamRank?.rank_change ?? 0)}
                  isValueSub
                  isTeamHeader
                  background="white"
                />
                {competitionKind === 'race' ? (
                  <RaceRankDisplay
                    competitionId={Number(competitionId)}
                    teamId={Number(teamId)}
                  />
                ) : (
                  <MatchRankDisplay
                    leagueRank={teamRank?.league_rank}
                    leagueRankChange={teamRank?.league_rank_change}
                  />
                )}
                <Stat
                  title="Rating"
                  value={teamRank?.rating ? teamRank.rating.toFixed(2) : null}
                  icon={IconStar}
                  background="white"
                  onClick={() => openPointBreakdown()}
                />
              </div>
            )}
          </div>
          <UpdatedBar />
        </div>
        <Breakdown
          opened={pointBreakdownOpen}
          close={closePointBreakdown}
          competitionId={Number(competitionId)}
          teamId={Number(teamId)}
          subject={'rating'}
        />
      </div>
    )
  }

  return (
    <div className={classes.container_wrapper_mobile}>
      <Meta title={team_name} />
      <div className={classes.team_header_container_mobile}>
        <div className={classes.header_container_wrapper}>
          <IconChevronLeft
            className={clsx(classes.back_icon, {
              [classes.back_icon_mobile]: !isDesktop,
            })}
            onClick={() =>
              navigate(
                location.state?.from?.pathname ||
                  `/competitions/${Number(competitionId)}/teams`
              )
            }
          />

          <div className={classes.notifications_container}>
            <Button
              className={clsx(classes.compare_button, {
                [classes.compare_button_mobile]: !isDesktop,
              })}
              leftSection={
                <IconGitCompare
                  className={clsx(classes.compare_icon, {
                    [classes.compare_icon_mobile]: !isDesktop,
                  })}
                />
              }
              onClick={() =>
                navigate(
                  `/competitions/${competitionId}/compare?ltype=team&lid=${teamId}`
                )
              }
            >
              Compare
            </Button>
            <IconBell className={classes.notifications_icon} />
          </div>
        </div>
        <div
          className={clsx(classes.team_header_wrapper, {
            [classes.team_header_wrapper_mobile]: !isDesktop,
          })}
        >
          <AspectRatio
            ratio={1 / 1}
            className={
              showPlayerDisplay ? classes.aspect_ratio_player : undefined
            }
          >
            <Image
              src={displayImage}
              alt={displayName}
              className={
                showPlayerDisplay ? classes.player_image : classes.team_logo
              }
              fit={isPlaceholderPhoto ? 'contain' : 'scale-down'}
            />
          </AspectRatio>
          <div className={classes.team_info_container}>
            <Text component="h3" className={classes.team_name_header}>
              {displayName}
            </Text>
            {team_location && (
              <Text className={classes.team_location}>
                <IconMapPin size="12" /> {team_location}
              </Text>
            )}
          </div>
        </div>
      </div>
      <UpdatedBar />
      <Breakdown
        opened={pointBreakdownOpen}
        close={closePointBreakdown}
        competitionId={Number(competitionId)}
        teamId={Number(teamId)}
        subject={'rating'}
      />
    </div>
  )
}
